import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { PagingParams, pagedResponse } from "./paging.js";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
import { LanguageSchema } from "./column-schemas.js";
/**
 * `risk_templates` carries no `client_id` — it is the shared catalog every
 * client's risk instances are generated from, not a per-tenant row. Access
 * is gated to the internal safety-catalog roles (ohs-admin/sifa/ama/
 * external-sifa/isAdmin), not client-scoped like `admin-locations`.
 */
/** A comma-joined value list on the wire, decoded to an array — the same CSV encoding `insights.ListEmployeesParams.location_ids` uses for ids. */
const StringCsvList = S.transform(S.String, S.Array(S.String), {
    strict: true,
    decode: (s) => s
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t.length > 0),
    encode: (values) => values.join(","),
});
/** The risk catalog's language, which is the database's `language` enum. */
export const RiskLanguage = LanguageSchema;
export const RiskTemplateStatus = S.Literal("draft", "approved");
export const RiskTemplateTagRow = S.Struct({
    id: S.Int,
    translation_group_id: S.Int,
    value: S.String,
});
export const RiskTemplateLawRow = S.Struct({
    id: S.Int,
    translation_group_id: S.Int,
    title: S.String,
    description: S.String,
    external_link: S.NullOr(S.String),
});
export const RiskTemplateAttachmentRow = S.Struct({
    id: S.UUID,
    filename: S.NullOr(S.String),
    size: S.NullOr(S.String),
    type: S.NullOr(S.String),
});
export const RiskTemplateRow = S.Struct({
    id: S.UUID,
    language: RiskLanguage,
    translation_group_id: S.NullOr(S.Int),
    title: S.String,
    description: S.String,
    status: RiskTemplateStatus,
    area_translation_group_id: S.Int,
    object_translation_group_id: S.Int,
    activity_translation_group_id: S.Int,
    factor_translation_group_id: S.Int,
    subfactor_translation_group_id: S.Int,
    source_translation_group_id: S.NullOr(S.Int),
    mode_translation_group_id: S.NullOr(S.Int),
    area: S.NullOr(S.String),
    object: S.NullOr(S.String),
    activity: S.NullOr(S.String),
    factor: S.NullOr(S.String),
    subfactor: S.NullOr(S.String),
    source: S.NullOr(S.String),
    mode: S.NullOr(S.String),
    laws: S.Array(RiskTemplateLawRow),
    tags: S.Array(RiskTemplateTagRow),
    attachments: S.Array(RiskTemplateAttachmentRow),
    created_by: S.NullOr(S.UUID),
    created_by_email: S.NullOr(S.String),
    created_by_first_name: S.NullOr(S.String),
    created_by_last_name: S.NullOr(S.String),
    date_created: S.NullOr(S.String),
    date_updated: S.NullOr(S.String),
});
export const RiskTemplateNotFound = S.Struct({
    _tag: S.Literal("RiskTemplateNotFound"),
});
export const ListRiskTemplatesParams = S.extend(PagingParams, S.Struct({
    language: S.optional(RiskLanguage),
    status: S.optional(RiskTemplateStatus),
    search: S.optional(S.String),
    // Text-equality filters on the view's own resolved area/object/.../source
    // columns — the risk-center's environment/factor/subfactor/source
    // dropdowns match by translated value, not by translation-group id.
    area: S.optional(StringCsvList),
    object: S.optional(StringCsvList),
    activity: S.optional(StringCsvList),
    factor: S.optional(StringCsvList),
    subfactor: S.optional(StringCsvList),
    source: S.optional(StringCsvList),
}));
export const ListRiskTemplates = HttpApiEndpoint.get("listRiskTemplates", "/admin/risk-templates")
    .setUrlParams(ListRiskTemplatesParams)
    .addSuccess(pagedResponse(RiskTemplateRow))
    .addError(Forbidden, { status: 403 });
export const ListRiskTemplatesByTranslationGroupsPayload = S.Struct({
    translation_group_ids: S.Array(S.Int),
});
/**
 * Bulk-by-translation-group read model for the offline inspection sync
 * engine's cache refresh — resolves every language variant of a batch of
 * risk-template translation groups in one round trip. Ids ride the POST
 * body rather than a query string so the list can exceed URL length limits.
 */
export const ListRiskTemplatesByTranslationGroups = HttpApiEndpoint.post("listRiskTemplatesByTranslationGroups", "/admin/risk-templates/by-translation-groups")
    .setPayload(ListRiskTemplatesByTranslationGroupsPayload)
    .addSuccess(S.Struct({ items: S.Array(RiskTemplateRow) }))
    .addError(Forbidden, { status: 403 });
/**
 * A risk template's area/object/activity/factor/source/mode is picked from
 * one of these seven small reference tables, each keyed by
 * `translation_group_id` with one row per language. Subfactors additionally
 * carry their parent factor's id. The offline sync engine caches every
 * language of all seven tables in one round trip to build the add-risk
 * modal's dropdowns without a server round trip per selection.
 */
export const RiskCategorizationOptionRow = S.Struct({
    id: S.Int,
    translation_group_id: S.NullOr(S.Int),
    language: RiskLanguage,
    value: S.String,
    date_created: S.NullOr(S.String),
    date_updated: S.NullOr(S.String),
});
export const RiskCategorizationSubfactorRow = S.extend(RiskCategorizationOptionRow, S.Struct({ factor_id: S.Int }));
export const RiskCategorizationOptions = S.Struct({
    areas: S.Array(RiskCategorizationOptionRow),
    objects: S.Array(RiskCategorizationOptionRow),
    activities: S.Array(RiskCategorizationOptionRow),
    factors: S.Array(RiskCategorizationOptionRow),
    subfactors: S.Array(RiskCategorizationSubfactorRow),
    sources: S.Array(RiskCategorizationOptionRow),
    modes: S.Array(RiskCategorizationOptionRow),
});
export const ListRiskCategorizationOptions = HttpApiEndpoint.get("listRiskCategorizationOptions", "/admin/risk-templates/categorization-options")
    .addSuccess(S.Struct({ data: RiskCategorizationOptions }))
    .addError(Forbidden, { status: 403 });
export const GetRiskTemplateParams = S.Struct({ id: S.UUID });
export const GetRiskTemplate = HttpApiEndpoint.get("getRiskTemplate", "/admin/risk-templates/get")
    .setUrlParams(GetRiskTemplateParams)
    .addSuccess(S.Struct({ data: RiskTemplateRow }))
    .addError(RiskTemplateNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const CreateRiskTemplatePayload = S.Struct({
    language: RiskLanguage,
    title: S.NonEmptyString,
    description: S.String,
    status: S.optionalWith(RiskTemplateStatus, { default: () => "draft" }),
    translation_group_id: S.optional(S.Int),
    area_translation_group_id: S.Int,
    object_translation_group_id: S.Int,
    activity_translation_group_id: S.Int,
    factor_translation_group_id: S.Int,
    subfactor_translation_group_id: S.Int,
    source_translation_group_id: S.optional(S.Int),
    mode_translation_group_id: S.optional(S.Int),
});
export const CreateRiskTemplate = HttpApiEndpoint.post("createRiskTemplate", "/admin/risk-templates/create")
    .setPayload(CreateRiskTemplatePayload)
    .addSuccess(S.Struct({ data: RiskTemplateRow }))
    .addError(Forbidden, { status: 403 });
export const UpdateRiskTemplatePayload = S.Struct({
    id: S.UUID,
    title: S.optional(S.NonEmptyString),
    description: S.optional(S.String),
    status: S.optional(RiskTemplateStatus),
    area_translation_group_id: S.optional(S.Int),
    object_translation_group_id: S.optional(S.Int),
    activity_translation_group_id: S.optional(S.Int),
    factor_translation_group_id: S.optional(S.Int),
    subfactor_translation_group_id: S.optional(S.Int),
    source_translation_group_id: S.optional(S.NullOr(S.Int)),
    mode_translation_group_id: S.optional(S.NullOr(S.Int)),
});
export const UpdateRiskTemplate = HttpApiEndpoint.post("updateRiskTemplate", "/admin/risk-templates/update")
    .setPayload(UpdateRiskTemplatePayload)
    .addSuccess(S.Struct({ data: RiskTemplateRow }))
    .addError(RiskTemplateNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const DeleteRiskTemplatePayload = S.Struct({ id: S.UUID });
export const DeleteRiskTemplate = HttpApiEndpoint.post("deleteRiskTemplate", "/admin/risk-templates/delete")
    .setPayload(DeleteRiskTemplatePayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(RiskTemplateNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
/**
 * Retires every language variant of one risk-template translation group at
 * once — the admin risk-center's draft-row bulk delete, replacing the
 * `itemsApi.list` + `itemsApi.deleteMany` workaround the screen used.
 */
export const DeleteRiskTemplatesByTranslationGroupPayload = S.Struct({
    translation_group_id: S.Int,
});
export const DeleteRiskTemplatesByTranslationGroup = HttpApiEndpoint.post("deleteRiskTemplatesByTranslationGroup", "/admin/risk-templates/delete-by-translation-group")
    .setPayload(DeleteRiskTemplatesByTranslationGroupPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Int }) }))
    .addError(Forbidden, { status: 403 });
/**
 * Approves every language variant of one risk-template translation group at
 * once — a template's approval status stays in lockstep across its
 * languages, the same grouping `DeleteRiskTemplatesByTranslationGroup` uses.
 */
export const ApproveRiskTemplatesByTranslationGroupPayload = S.Struct({
    translation_group_id: S.Int,
});
export const ApproveRiskTemplatesByTranslationGroup = HttpApiEndpoint.post("approveRiskTemplatesByTranslationGroup", "/admin/risk-templates/approve-by-translation-group")
    .setPayload(ApproveRiskTemplatesByTranslationGroupPayload)
    .addSuccess(S.Struct({ data: S.Struct({ approved: S.Int }) }))
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// Link-table management — a risk template's tags/laws/measure-templates
// reference existing catalog/measure-template rows by translation-group or
// measure-template id; attachments reference an already-uploaded file.
// ---------------------------------------------------------------------------
export const AddRiskTemplateTagPayload = S.Struct({
    template_id: S.UUID,
    tag_group_id: S.Int,
});
export const AddRiskTemplateTag = HttpApiEndpoint.post("addRiskTemplateTag", "/admin/risk-templates/tags/add")
    .setPayload(AddRiskTemplateTagPayload)
    .addSuccess(S.Struct({ data: S.Struct({ id: S.Int }) }))
    .addError(RiskTemplateNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const RemoveRiskTemplateTagPayload = S.Struct({
    template_id: S.UUID,
    tag_group_id: S.Int,
});
export const RemoveRiskTemplateTag = HttpApiEndpoint.post("removeRiskTemplateTag", "/admin/risk-templates/tags/remove")
    .setPayload(RemoveRiskTemplateTagPayload)
    .addSuccess(S.Struct({ data: S.Struct({ removed: S.Int }) }))
    .addError(Forbidden, { status: 403 });
export const AddRiskTemplateLawPayload = S.Struct({
    template_id: S.UUID,
    law_group_id: S.Int,
});
export const AddRiskTemplateLaw = HttpApiEndpoint.post("addRiskTemplateLaw", "/admin/risk-templates/laws/add")
    .setPayload(AddRiskTemplateLawPayload)
    .addSuccess(S.Struct({ data: S.Struct({ id: S.Int }) }))
    .addError(RiskTemplateNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const RemoveRiskTemplateLawPayload = S.Struct({
    template_id: S.UUID,
    law_group_id: S.Int,
});
export const RemoveRiskTemplateLaw = HttpApiEndpoint.post("removeRiskTemplateLaw", "/admin/risk-templates/laws/remove")
    .setPayload(RemoveRiskTemplateLawPayload)
    .addSuccess(S.Struct({ data: S.Struct({ removed: S.Int }) }))
    .addError(Forbidden, { status: 403 });
export const AddRiskTemplateMeasureLinkPayload = S.Struct({
    risk_template_id: S.UUID,
    measure_template_id: S.UUID,
});
export const AddRiskTemplateMeasureLink = HttpApiEndpoint.post("addRiskTemplateMeasureLink", "/admin/risk-templates/measure-links/add")
    .setPayload(AddRiskTemplateMeasureLinkPayload)
    .addSuccess(S.Struct({ data: S.Struct({ id: S.UUID }) }))
    .addError(RiskTemplateNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
/**
 * The offline sync engine's `copyMeasureTemplateLinksToRiskInstance` needs
 * a template's linked measure-template ids to seed a newly-created risk
 * instance's own measure links — a plain id list, not the full
 * `RiskTemplateRow` (which carries no measure-link field at all).
 */
export const RiskTemplateMeasureLinkRow = S.Struct({
    measure_template_id: S.UUID,
});
export const ListRiskTemplateMeasureLinksParams = S.Struct({
    template_id: S.UUID,
});
export const ListRiskTemplateMeasureLinks = HttpApiEndpoint.get("listRiskTemplateMeasureLinks", "/admin/risk-templates/measure-links")
    .setUrlParams(ListRiskTemplateMeasureLinksParams)
    .addSuccess(S.Struct({ items: S.Array(RiskTemplateMeasureLinkRow) }))
    .addError(Forbidden, { status: 403 });
/**
 * Bulk-by-template read model for the offline inspection sync engine's cache
 * refresh. `refreshLinkedMeasureTemplates` needs the measure links of every
 * cached risk template, and the by-id read model above forced one round trip
 * per template — hundreds of sequential requests that blocked the app's
 * "Initializing offline storage" screen for minutes and re-fired on every
 * full refresh. Ids ride the POST body so the set can exceed a URL length
 * limit, mirroring `ListRiskTemplatesByTranslationGroups`. Rows carry their
 * own `risk_template_id` so the caller can scope reconciliation per template.
 */
export const RiskTemplateMeasureLinkByTemplateRow = S.Struct({
    risk_template_id: S.UUID,
    measure_template_id: S.UUID,
});
/** Maximum number of risk-template ids accepted by the bulk link read. */
export const RISK_TEMPLATE_MEASURE_LINK_BATCH_SIZE = 500;
export const ListRiskTemplateMeasureLinksByTemplatesPayload = S.Struct({
    risk_template_ids: S.Array(S.UUID).pipe(S.maxItems(RISK_TEMPLATE_MEASURE_LINK_BATCH_SIZE)),
});
export const ListRiskTemplateMeasureLinksByTemplates = HttpApiEndpoint.post("listRiskTemplateMeasureLinksByTemplates", "/admin/risk-templates/measure-links/by-templates")
    .setPayload(ListRiskTemplateMeasureLinksByTemplatesPayload)
    .addSuccess(S.Struct({ items: S.Array(RiskTemplateMeasureLinkByTemplateRow) }))
    .addError(Forbidden, { status: 403 });
export const RemoveRiskTemplateMeasureLinkPayload = S.Struct({
    risk_template_id: S.UUID,
    measure_template_id: S.UUID,
});
export const RemoveRiskTemplateMeasureLink = HttpApiEndpoint.post("removeRiskTemplateMeasureLink", "/admin/risk-templates/measure-links/remove")
    .setPayload(RemoveRiskTemplateMeasureLinkPayload)
    .addSuccess(S.Struct({ data: S.Struct({ removed: S.Int }) }))
    .addError(Forbidden, { status: 403 });
export const AddRiskTemplateAttachmentPayload = S.Struct({
    template_id: S.UUID,
    file_id: S.UUID,
});
export const AddRiskTemplateAttachment = HttpApiEndpoint.post("addRiskTemplateAttachment", "/admin/risk-templates/attachments/add")
    .setPayload(AddRiskTemplateAttachmentPayload)
    .addSuccess(S.Struct({ data: S.Struct({ id: S.Int }) }))
    .addError(RiskTemplateNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const RemoveRiskTemplateAttachmentPayload = S.Struct({
    template_id: S.UUID,
    file_id: S.UUID,
});
export const RemoveRiskTemplateAttachment = HttpApiEndpoint.post("removeRiskTemplateAttachment", "/admin/risk-templates/attachments/remove")
    .setPayload(RemoveRiskTemplateAttachmentPayload)
    .addSuccess(S.Struct({ data: S.Struct({ removed: S.Int }) }))
    .addError(Forbidden, { status: 403 });
export const adminRiskTemplatesGroup = HttpApiGroup.make("admin-risk-templates")
    .add(ListRiskTemplates)
    .add(ListRiskTemplatesByTranslationGroups)
    .add(ListRiskCategorizationOptions)
    .add(GetRiskTemplate)
    .add(CreateRiskTemplate)
    .add(UpdateRiskTemplate)
    .add(DeleteRiskTemplate)
    .add(DeleteRiskTemplatesByTranslationGroup)
    .add(ApproveRiskTemplatesByTranslationGroup)
    .add(AddRiskTemplateTag)
    .add(RemoveRiskTemplateTag)
    .add(AddRiskTemplateLaw)
    .add(RemoveRiskTemplateLaw)
    .add(AddRiskTemplateMeasureLink)
    .add(RemoveRiskTemplateMeasureLink)
    .add(ListRiskTemplateMeasureLinks)
    .add(ListRiskTemplateMeasureLinksByTemplates)
    .add(AddRiskTemplateAttachment)
    .add(RemoveRiskTemplateAttachment)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=admin-risk-templates.js.map